import { ProgramFormConstant } from './program-form.constant';
import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DynamicFormService } from '@shared/components/dynamic-form/dynamic-form.service';
import { EventService } from '@shared/event.service';
import { BaseRestApiService } from '@shared/services/base-rest-api.service';
import { RestApiWrapperService } from '@shared/services/rest-api-wrapper.service';
import { OAuthService } from 'angular-oauth2-oidc';
import * as FileSaver from 'file-saver';
import { Observable } from 'rxjs';

export enum FORM_ID {
  PRE_QUALIFICATION = 'PRE_QUALIFICATION',
  BUSINESS_PLAN = 'BUSINESS_PLAN',
  CHECKLIST = 'CHECKLIST',
  PROJECT_APPLICATION = 'PROJECT_APPLICATION'
};

const formFilesList = {
  preQualification: 'Form-Pre-Qualification.json',
  portfolioDescription: 'Form-Portfolio-Description.json',
  portfolioDocumentChecklist: 'Form-Portfolio-Document-Checklist.json',
  projectApplication: 'Form-Project-Application.json'
};

export const getFormFilesList = () => {
  return formFilesList;
};

export const findFormId = (fileName: string): FORM_ID => {
  let result: FORM_ID;
  switch (fileName) {
    // not in formFilesList so it doesn't show up in 'Forms' tab
    case 'Form-Pre-Qualification.json':
      result = FORM_ID.PRE_QUALIFICATION;
      break;
    case formFilesList['portfolioDescription']:
      result = FORM_ID.BUSINESS_PLAN;
      break;
    case formFilesList['portfolioDocumentChecklist']:
      result = FORM_ID.CHECKLIST;
      break;
    case formFilesList['projectApplication']:
      result = FORM_ID.PROJECT_APPLICATION;
      break;
  }
  return result;
};

// Note: Perhaps this service goes to Form Manager
@Injectable()
export class ProgramFormService extends BaseRestApiService  {
  constructor(_restApiWrapperService: RestApiWrapperService,
              private _dynamicFormService: DynamicFormService,
              private _eventService: EventService,
              private _translateService: TranslateService,
              private _oauthService: OAuthService) {
    super(_restApiWrapperService);
  }

  /**
   * Upload (PUT) a form template (for Investor)
   * @param programId: string
   * @param fileName: string
   * @param stageConfigId: optional - for forms that can be in multiple stages
   * @param json: {}
   */
  public uploadFormTemplate(programId: string, fileName: string, stageConfigId: string, json: {}): Observable<any> {
   let jsonObject = this._dynamicFormService.removeBlankNodes({...json});
    const formId = findFormId(fileName);
    if (formId) {
      // add Validate button before Submit button
      jsonObject = this._dynamicFormService.checkValidateButton(jsonObject);
      // add submit button when the form is pre-qualification
      if (fileName === getFormFilesList()['preQualification']) {
        jsonObject = this._dynamicFormService.checkSubmitButton(jsonObject);
      }
      // add save button to all forms
      jsonObject = this._dynamicFormService.checkSaveButton(jsonObject);
      const file = new File([JSON.stringify(jsonObject)], fileName, {type: 'text/json'});
      const formData = new FormData();
      formData.append('file', file);
      if (stageConfigId) {
        return this.put(ProgramFormConstant.form.detailForStage(programId, stageConfigId, formId), formData);
      }
      return this.put(ProgramFormConstant.form.detail(programId, formId), formData);
    } else {
      this._eventService.error(this._translateService.instant('error-message.file-name-not-supported'));
    }
  }

  uploadMilestoneForm(programId: string, milestoneConfigId: string, fileName: string, json: {}): Observable<any> {
    let jsonObject = this._dynamicFormService.removeBlankNodes({...json});
    // add Validate button before Submit button
    jsonObject = this._dynamicFormService.checkValidateButton(jsonObject);
    // add submit button
    jsonObject = this._dynamicFormService.checkSubmitButton(jsonObject);
    // add save button to all forms
    jsonObject = this._dynamicFormService.checkSaveButton(jsonObject);
    jsonObject = this._dynamicFormService.saveOriginalFileName(jsonObject, fileName);
    const file = new File([JSON.stringify(jsonObject)], fileName, {type: 'text/json'});
    const formData = new FormData();
    formData.append('file', file);
    return this.put(ProgramFormConstant.milestoneForms.create(programId, milestoneConfigId), formData);
  }

  deleteMilestoneForm(programId: string, milestoneConfigId: string): Observable<any> {
    return this.delete(ProgramFormConstant.milestoneForms.delete(programId, milestoneConfigId));
  }

  downloadMilestoneForm(formContent: string, name: string): void {
    const blob = new Blob([formContent], { type: 'application/json' });
    FileSaver.saveAs(blob, name);
  }

  public getForm(programId: string, formId: FORM_ID): Observable<any> {
    return this.get(ProgramFormConstant.form.detail(programId, formId));
  }

  public getStagedFormForm(programId: string, stageId: string, formId: FORM_ID): Observable<any> {
    return this.get(ProgramFormConstant.form.jsonDetailForStage(programId, stageId, formId));
  }

  public downloadFormTemplate(programId: string, formId: FORM_ID,  name: string): void {
    const url = ProgramFormConstant.form.detail(programId, formId);
    return this.downloadFormTemplateWithUrl(url, name);
  }

  public getFormTemplateStatusForStage(programId: string, stageConfigId: string, formId: FORM_ID): Observable<any> {
    return this.get(ProgramFormConstant.form.statusForStage(programId, stageConfigId, formId));
  }

  public downloadFormTemplateForStage(programId: string, formId: FORM_ID,  stageConfigId: string, name: string): void {
    const url = ProgramFormConstant.form.detailForStage(programId, stageConfigId, formId);
    return this.downloadFormTemplateWithUrl(url, name);
  }

  private downloadFormTemplateWithUrl(url: string, name: string): void {
    const options = {
      headers: new HttpHeaders().set('Authorization', 'Bearer ' + this._oauthService.getAccessToken()),
      responseType: 'blob' as const
    };

    this.get(url, options, true)
    .subscribe((response: any) => {
      FileSaver.saveAs(response, name);
    });
  }
}
